import React, { FC } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Icon,
  InputAdornment,
  LinearProgress,
  Tab,
  Tabs,
  TextField,
  useMediaQuery
} from '@mui/material';
import { LegacyGrid as Grid } from '../../../../elements';
import { V4_DIALOG_CONTENT, V4_DIALOG_MD, V4_SMALL_SCREEN } from '../../../../theme/v4Compat';
import { AnimalType } from '../../../../models/animal';
import { MedicalSuppliesService } from '../../../../hooks';
import { useTranslation } from 'react-i18next';
import { DiseaseViewModel, FarmMedicalSupplyViewModel, NotificationRuleRegionType } from '../../../../models/injection';
import { extractApiErrorMessage, notifications } from '../../../../utils';
import {
  CreateNotificationRuleCommand,
  DayOfWeek,
  LungWeatherRuleDataForCreation,
  lungWeatherRuleDefaults,
  windSensitivities,
  NotificationRuleViewModel,
  PercentageRuleData,
  RuleType,
  UpdateNotificationRuleCommand,
  ValidationPeriodData,
  WindSensitivity
} from '../../../../models/notification-rule';
import NotificationRulesService from '../../../../hooks/NotificationRulesService';
import Skeleton from '@mui/material/Skeleton';
import { NotificationRuleRegionSelect } from '../../../../elements';
import { UserViewModel } from '../../../../models/user';
import { IAgeRule } from '../tabs/age-rule';
import UsersStaffSelect from '../UsersStaffSelect';
import { LungWeatherRuleTab, MinAnimalRegTab, NumberRuleTab, PercentageRuleTab } from '../tabs';
import useFeatures, { FeatureType } from '../../../../hooks/features';
import WeekDaySelect from '../WeekDaySelect';

interface INotificationRuleDialogProps {
  callback?: () => void;
  open: boolean;
  setOpen: (value: boolean) => void;
  mode: 'create' | 'update';
  rule: NotificationRuleViewModel | null;
}

const NotificationRuleDialog: FC<INotificationRuleDialogProps> = (props) => {
  const { callback, open, mode, rule, setOpen } = props;

  const { t } = useTranslation();
  const fullScreen = useMediaQuery(V4_SMALL_SCREEN);
  const { hasFeature } = useFeatures();

  // With the flag off the correlation tab is not rendered at all, so neither create nor update can reach it.
  const lungWeatherEnabled = hasFeature(FeatureType.USE_LUNG_WEATHER_RULE);

  const medicalSuppliesService = new MedicalSuppliesService();
  const notificationRuleService = new NotificationRulesService();

  const [isLoading, setIsLoading] = React.useState(false);
  const [selectedTab, setSelectedTab] = React.useState<number>(0);

  const [farmSupplies, setFarmSupplies] = React.useState([] as FarmMedicalSupplyViewModel[]);
  const [farmDiseases, setFarmDiseases] = React.useState([] as DiseaseViewModel[]);
  const [loadingMedSupp, setLoadingMedSupp] = React.useState<boolean>(false);
  const [loadingDiseases, setLoadingDiseases] = React.useState<boolean>(false);
  const [isPregnant, setIsPregnant] = React.useState<boolean | undefined>(undefined);
  const [isLactating, setIsLactating] = React.useState<boolean | undefined>(undefined);
  const [dayOfWeek, setDayOfWeek] = React.useState<DayOfWeek>(DayOfWeek.Monday);
  const [weekNumber, setWeekNumber] = React.useState<string>('1');
  const [emails, setEmails] = React.useState<UserViewModel[]>([]);

  // For percent tab
  const [animalType, setAnimalType] = React.useState<AnimalType>(AnimalType.Unknown);
  const [farmSupplyId, setFarmSupplyId] = React.useState<string>('');
  const [farmDiseaseId, setFarmDiseaseId] = React.useState<string>('');
  const [notificationRuleRegionType, setNotificationRuleRegionType] = React.useState<NotificationRuleRegionType>(
    NotificationRuleRegionType.Injection
  );
  const [ageRules, setAgeRules] = React.useState<IAgeRule[]>([{ from: '', till: '', percentage: '' }]);

  // For number tab
  const [minInjected, setMinInjected] = React.useState<string>('');

  // For min registered tab
  const [minRegistered, setMinRegistered] = React.useState<string>('');

  // For lung weather correlation tab
  const [peakThreshold, setPeakThreshold] = React.useState<string>('');
  const [lookbackDays, setLookbackDays] = React.useState<string>(lungWeatherRuleDefaults.lookbackDays.toString());
  const [forecastDays, setForecastDays] = React.useState<string>(lungWeatherRuleDefaults.forecastDays.toString());
  const [sensitivity, setSensitivity] = React.useState<WindSensitivity>(lungWeatherRuleDefaults.sensitivity);

  const loadFarmMedicalSupplies = async () => {
    setLoadingMedSupp(true);

    medicalSuppliesService
      .getFarmMedicalSupplies()
      .then((supplies) => {
        setFarmSupplies(supplies);
        setLoadingMedSupp(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoadingMedSupp(false);
      });
  };

  const loadFarmDiseases = async () => {
    setLoadingDiseases(true);

    medicalSuppliesService
      .getFarmAllDiseases()
      .then((diseases) => {
        setFarmDiseases(diseases);
        setLoadingDiseases(false);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setLoadingDiseases(false);
      });
  };

  /**
   * The lung weather rule data, built in one place and reused by validation, create and update so the three
   * cannot drift apart the way the older tabs' inline copies have.
   */
  const buildLungWeatherRuleData = (): LungWeatherRuleDataForCreation => {
    const canBeBreeding = animalType === AnimalType.Sow || animalType === AnimalType.Gilt;

    return {
      farmDiseaseId: farmDiseaseId,
      // farmMedicalSupplyId is deliberately never sent. The medication that counts towards a peak is the one
      // configured on the disease; the backend ignores this field, so setting it would only mislead.
      animalType: animalType,
      // The backend rejects both outright unless the animal type can actually be pregnant or lactating.
      isPregnant: canBeBreeding && typeof isPregnant === 'boolean' ? isPregnant : undefined,
      isLactating: canBeBreeding && typeof isLactating === 'boolean' ? isLactating : undefined,
      peakThreshold: Number(peakThreshold),
      lookbackDays: Number(lookbackDays),
      forecastDays: Number(forecastDays),
      sensitivity: sensitivity
    };
  };

  const validateLungWeatherData = (data: LungWeatherRuleDataForCreation): string[] => {
    const errors: string[] = [];

    // Unlike the other rule types the disease is mandatory: the pattern is learned per disease, per stable.
    if (isValidDiseaseId(data.farmDiseaseId) === false) errors.push(t('f-x-is-req', { name: t('f-disease') }));

    if (!data.peakThreshold || data.peakThreshold <= 0)
      errors.push(t('x-should-be-higher-than-y', { name: t('f-notif-rule-peak-threshold'), value: 0 }));

    if (!data.lookbackDays || data.lookbackDays < 1 || data.lookbackDays > 30)
      errors.push(t('f-val-x-between', { name: t('f-notif-rule-lookback-days'), min: 1, max: 30 }));

    // Open-Meteo serves at most 16 forecast days, and day one is today.
    if (!data.forecastDays || data.forecastDays < 2 || data.forecastDays > 16)
      errors.push(t('f-val-x-between', { name: t('f-notif-rule-forecast-days'), min: 2, max: 16 }));

    if (!data.sensitivity || windSensitivities.includes(data.sensitivity) === false)
      errors.push(t('f-x-is-req', { name: t('f-notif-rule-sensitivity') }));

    return errors;
  };

  const validateForm = (): boolean => {
    switch (selectedTab) {
      case 0:
        // For percentage tab there should be possibility to create new multiples rules at one time
        const percentageRules: CreateNotificationRuleCommand[] = [];
        ageRules.forEach((ar) => {
          percentageRules.push({
            ruleData: {
              percentageRuleData: {
                percentage: Number(ar.percentage),
                ageFrom: Number(ar.from),
                ageTill: Number(ar.till),
                isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
                isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
                animalType: animalType,
                farmMedicalSupplyId: farmSupplyId,
                farmDiseaseId: farmDiseaseId
              }
            },
            ruleType: RuleType.PercentageLimit,
            emailSettings: {
              userIds: [...emails.map((u) => u.id)]
            }
          });
        });

        let errors: string[] = [];
        percentageRules.forEach((r) => {
          const data = r.ruleData.percentageRuleData;
          if (data) {
            const errorList = validatePercentRuleData(data);
            if (errorList.length > 0) errors = [...errors, ...errorList];
          }
        });

        if (errors.length > 0) {
          notifications.error(errors.join('; '));
          return false;
        }

        break;

      case 1:
        const numRule: CreateNotificationRuleCommand = {
          ruleData: {
            numRuleData: {
              isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
              isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
              farmMedicalSupplyId: farmSupplyId,
              farmDiseaseId: farmDiseaseId,
              animalType: animalType,
              minInjected: Number(minInjected)
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.NumLimit,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        let numRuleErrors: string[] = [];

        const d = numRule.ruleData.numRuleData;

        if (isValidMedicalSupplyId(d?.farmMedicalSupplyId) === false && isValidDiseaseId(d?.farmDiseaseId) === false)
          numRuleErrors.push(t('f-x-is-req-at-least-one', { name: t('f-supp-farm'), name2: t('f-disease') }));

        if (!d?.minInjected || d?.minInjected <= 0)
          numRuleErrors.push(t('f-x-is-req', { name: t('f-supp-rule-anim-min') }));

        const pValid = validatePeriodData(numRule.periodData || {});
        if (pValid.length > 0) numRuleErrors = [...numRuleErrors, ...pValid];

        if (numRuleErrors.length > 0) {
          notifications.error(numRuleErrors.join('; '));
          return false;
        }

        break;

      case 2:
        const minRegRule: CreateNotificationRuleCommand = {
          ruleData: {
            minRegAnimalsRuleData: {
              minRegistered: Number(minRegistered),
              animalType: animalType
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.MinRegistered,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        let minRegErrors: string[] = [];
        const periodVal = validatePeriodData(minRegRule.periodData || {});
        if (periodVal.length > 0) minRegErrors = [...minRegErrors, ...periodVal];

        const minReg: number | undefined = minRegRule.ruleData.minRegAnimalsRuleData?.minRegistered;
        if (!minReg || minReg <= 0) minRegErrors.push(t('f-x-is-req', { name: t('f-notif-rule-min-reg-title') }));

        if (minRegErrors.length > 0) {
          notifications.error(minRegErrors.join('; '));
          return false;
        }

        break;

      case 3: {
        // No period validation: this rule type carries no periodData. Its window is lookbackDays and it is
        // evaluated on every backend pass.
        const lungWeatherErrors = validateLungWeatherData(buildLungWeatherRuleData());

        if (lungWeatherErrors.length > 0) {
          notifications.error(lungWeatherErrors.join('; '));
          return false;
        }

        break;
      }
    }

    return true;
  };

  const handleCreate = () => {
    switch (selectedTab) {
      case 0:
        // For percentage tab there should be possibility to create new multiples rules at one time
        const percentageRules: CreateNotificationRuleCommand[] = [];
        ageRules.forEach((ar) => {
          percentageRules.push({
            ruleData: {
              percentageRuleData: {
                percentage: Number(ar.percentage),
                ageFrom: Number(ar.from),
                ageTill: Number(ar.till),
                isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
                isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
                animalType: animalType,
                farmMedicalSupplyId: farmSupplyId,
                farmDiseaseId: farmDiseaseId
              }
            },
            ruleType: RuleType.PercentageLimit,
            emailSettings: {
              userIds: [...emails.map((u) => u.id)]
            }
          });
        });

        setIsLoading(true);

        notificationRuleService
          .createNotificationRules({
            notificationRules: percentageRules
          })
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 1:
        const numRule: CreateNotificationRuleCommand = {
          ruleData: {
            numRuleData: {
              isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
              isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
              farmMedicalSupplyId: farmSupplyId,
              farmDiseaseId: farmDiseaseId,
              animalType: animalType,
              minInjected: Number(minInjected)
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.NumLimit,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .createNotificationRule(numRule)
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 2:
        const minRegRule: CreateNotificationRuleCommand = {
          ruleData: {
            minRegAnimalsRuleData: {
              minRegistered: Number(minRegistered),
              animalType: animalType
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.MinRegistered,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .createNotificationRule(minRegRule)
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 3: {
        const lungWeatherRule: CreateNotificationRuleCommand = {
          ruleData: {
            lungWeatherRuleData: buildLungWeatherRuleData()
          },
          // periodData is deliberately omitted: the backend derives this rule's window from lookbackDays and
          // rejects nothing for its absence, since it maps the type to ValidationPeriodType.Daily.
          ruleType: RuleType.LungWeatherCorrelation,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .createNotificationRule(lungWeatherRule)
          .then(() => {
            handleClose();
            if (callback) {
              callback();
            }
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
          })
          .finally(() => setIsLoading(false));
        break;
      }
    }
  };

  const handleUpdate = () => {
    if (rule === null) {
      return;
    }

    switch (selectedTab) {
      case 0:
        // For percentage tab there should be possibility to create new multiples rules at one time
        const percentageRules: UpdateNotificationRuleCommand[] = [];
        ageRules.forEach((ar) => {
          percentageRules.push({
            id: rule.id,
            ruleData: {
              percentageRuleData: {
                percentage: Number(ar.percentage),
                ageFrom: Number(ar.from),
                ageTill: Number(ar.till),
                isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
                isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
                animalType: animalType,
                farmMedicalSupplyId: farmSupplyId,
                farmDiseaseId: farmDiseaseId
              }
            },
            ruleType: RuleType.PercentageLimit,
            emailSettings: {
              userIds: [...emails.map((u) => u.id)]
            }
          });
        });

        setIsLoading(true);

        notificationRuleService
          .updateNotificationRules({
            notificationRules: percentageRules
          })
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 1:
        const numRule: UpdateNotificationRuleCommand = {
          id: rule.id,
          ruleData: {
            numRuleData: {
              isPregnant: typeof isPregnant === 'boolean' ? isPregnant : undefined,
              isLactating: typeof isLactating === 'boolean' ? isLactating : undefined,
              farmMedicalSupplyId: farmSupplyId,
              farmDiseaseId: farmDiseaseId,
              animalType: animalType,
              minInjected: Number(minInjected)
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.NumLimit,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .updateNotificationRule(numRule)
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 2:
        const minRegRule: UpdateNotificationRuleCommand = {
          id: rule.id,
          ruleData: {
            minRegAnimalsRuleData: {
              minRegistered: Number(minRegistered),
              animalType: animalType
            }
          },
          periodData: {
            weeksData: {
              weekCount: Number(weekNumber),
              dayOfWeek: Number(dayOfWeek)
            }
          },
          ruleType: RuleType.MinRegistered,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .updateNotificationRule(minRegRule)
          .then(() => {
            handleClose();
            callback && callback();
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(JSON.stringify(e));
          })
          .finally(() => setIsLoading(false));
        break;

      case 3: {
        const lungWeatherRule: UpdateNotificationRuleCommand = {
          id: rule.id,
          ruleData: {
            lungWeatherRuleData: buildLungWeatherRuleData()
          },
          // See handleCreate: no periodData for this rule type.
          ruleType: RuleType.LungWeatherCorrelation,
          emailSettings: {
            userIds: emails.map((u) => u.id)
          }
        };

        setIsLoading(true);

        notificationRuleService
          .updateNotificationRule(lungWeatherRule)
          .then(() => {
            handleClose();
            if (callback) {
              callback();
            }
            notifications.success(t('f-supp-rule-add-ok'));
          })
          .catch((e) => {
            notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
          })
          .finally(() => setIsLoading(false));
        break;
      }
    }
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    if (mode === 'create') {
      handleCreate();
    } else if (mode === 'update') {
      handleUpdate();
    }
  };

  const renderSubmitButtonText = () => {
    if (mode === 'create') {
      return t('f-com-add');
    } else if (mode === 'update') {
      return t('f-com-edit');
    }
  };

  const resetNotificationRuleRegionType = (value: NotificationRuleRegionType) => {
    setSelectedTab(0);
    setNotificationRuleRegionType(value);
  };

  React.useEffect(() => {
    if (mode === 'update' && rule !== null) {
      setDayOfWeek(rule.periodData?.weeksData?.dayOfWeek || DayOfWeek.Monday);
      setWeekNumber(rule.periodData?.weeksData?.weekCount.toString() || '1');

      let animalType: AnimalType | undefined = undefined;
      let farmMedicalSupplyId: string | undefined = undefined;
      let farmDiseaseId: string | undefined = undefined;
      let isPregnant: boolean | undefined = undefined;
      let isLactating: boolean | undefined = undefined;
      let minInjected: string = '';
      let minRegistered: string = '';
      let ageFrom: string = '';
      let ageTill: string = '';
      let percentage: string = '';
      let tab: number = 0;

      if (rule.ruleType === RuleType.PercentageLimit) {
        animalType = rule.ruleData?.percentageRuleData?.animalType;
        ageFrom = rule.ruleData?.percentageRuleData?.ageFrom.toString() || '';
        ageTill = rule.ruleData?.percentageRuleData?.ageTill.toString() || '';
        percentage = rule.ruleData?.percentageRuleData?.percentage.toString() || '';
        isLactating = rule.ruleData?.percentageRuleData?.isPregnant;
        isPregnant = rule.ruleData?.percentageRuleData?.isLactating;
        farmMedicalSupplyId = rule.ruleData?.percentageRuleData?.farmMedicalSupplyId;
        farmDiseaseId = rule.ruleData?.percentageRuleData?.farmDiseaseId;
        tab = 0;
      }

      if (rule.ruleType === RuleType.NumLimit) {
        animalType = rule.ruleData?.numRuleData?.animalType;
        minInjected = rule.ruleData?.numRuleData?.minInjected?.toString() || '';
        isLactating = rule.ruleData?.numRuleData?.isPregnant;
        isPregnant = rule.ruleData?.numRuleData?.isLactating;
        farmMedicalSupplyId = rule.ruleData?.numRuleData?.farmMedicalSupplyId;
        farmDiseaseId = rule.ruleData?.numRuleData?.farmDiseaseId;
        tab = 1;
      }

      if (rule.ruleType === RuleType.MinRegistered) {
        animalType = rule.ruleData?.minRegAnimalsRuleData?.animalType;
        minRegistered = rule.ruleData?.minRegAnimalsRuleData?.minRegistered.toString() || '';
        tab = 2;
      }

      // The list hides these rules while the flag is off, so this branch is normally unreachable then; skipping
      // it keeps the dialog off a tab that no longer exists if one is opened some other way.
      if (rule.ruleType === RuleType.LungWeatherCorrelation && lungWeatherEnabled) {
        const lungWeatherData = rule.ruleData?.lungWeatherRuleData;

        animalType = lungWeatherData?.animalType;
        isPregnant = lungWeatherData?.isPregnant;
        isLactating = lungWeatherData?.isLactating;
        farmDiseaseId = lungWeatherData?.farmDiseaseId;
        // farmMedicalSupplyId is intentionally not read: this tab has no medication field, and picking it up
        // here would only feed it back on save.

        setPeakThreshold(lungWeatherData?.peakThreshold?.toString() || '');
        setLookbackDays((lungWeatherData?.lookbackDays ?? lungWeatherRuleDefaults.lookbackDays).toString());
        setForecastDays((lungWeatherData?.forecastDays ?? lungWeatherRuleDefaults.forecastDays).toString());
        setSensitivity((lungWeatherData?.sensitivity as WindSensitivity) ?? lungWeatherRuleDefaults.sensitivity);

        tab = 3;
      }

      if (farmMedicalSupplyId !== undefined && farmMedicalSupplyId !== null) {
        setFarmSupplyId(farmMedicalSupplyId);
        setNotificationRuleRegionType(NotificationRuleRegionType.Injection);
      }

      if (farmDiseaseId !== undefined && farmDiseaseId !== null) {
        setFarmDiseaseId(farmDiseaseId);
        setNotificationRuleRegionType(NotificationRuleRegionType.Mortality);
      }

      setAgeRules([{ from: ageFrom, till: ageTill, percentage: percentage }]);
      setAnimalType(animalType || AnimalType.Unknown);
      setMinInjected(minInjected);
      setMinRegistered(minRegistered);
      setIsLactating(isLactating);
      setIsPregnant(isPregnant);
      setSelectedTab(tab);
      setEmails(rule.emailSettings?.users || []);
    }

    if (mode === 'create') {
      setAnimalType(AnimalType.Unknown);
      setFarmSupplyId('');
      setFarmDiseaseId('');
      setAgeRules([{ from: '', till: '', percentage: '' }]);
      setMinInjected('');
      setMinRegistered('');
      setIsLactating(undefined);
      setIsPregnant(undefined);
      setDayOfWeek(DayOfWeek.Monday);
      setWeekNumber('1');
      setEmails([]);
      setNotificationRuleRegionType(NotificationRuleRegionType.Injection);
      setPeakThreshold('');
      setLookbackDays(lungWeatherRuleDefaults.lookbackDays.toString());
      setForecastDays(lungWeatherRuleDefaults.forecastDays.toString());
      setSensitivity(lungWeatherRuleDefaults.sensitivity);
      setSelectedTab(0);
    }
  }, [rule, mode, lungWeatherEnabled]);

  React.useEffect(() => {
    loadFarmMedicalSupplies().then();
    loadFarmDiseases().then();
  }, []);

  const handleClose = () => {
    setOpen(false);
  };

  const validatePercentRuleData = (data: PercentageRuleData): string[] => {
    const errors = [];

    if (data.ageFrom <= 0) errors.push(t('f-x-is-req', { name: t('f-notif-rule-from') }));

    if (data.ageTill <= 0) errors.push(t('x-should-be-higher-than-y', { name: t('f-notif-rule-till'), value: 0 }));

    if (data.ageFrom >= data.ageTill)
      errors.push(t('x-should-be-higher-than-y', { name: t('f-notif-rule-till'), value: t('f-notif-rule-from') }));

    if (data.animalType === null || data.animalType === undefined)
      errors.push(t('f-x-is-req', { name: t('m-animal-type') }));

    if (isValidMedicalSupplyId(data.farmMedicalSupplyId) === false && isValidDiseaseId(data.farmDiseaseId) === false)
      errors.push(t('f-x-is-req-at-least-one', { name: t('f-supp-farm'), name2: t('f-disease') }));

    if (data.percentage <= 0) errors.push(t('x-should-be-higher-than-y', { name: t('f-supp-rule-perc'), value: 0 }));

    if (data.percentage > 99) errors.push(t('x-should-be-lower-than-y', { name: t('f-supp-rule-perc'), value: 99 }));

    return errors;
  };

  const isValidMedicalSupplyId = (farmMedicalSupplyId: string | undefined): boolean => {
    if (farmMedicalSupplyId === undefined || farmMedicalSupplyId === '' || farmMedicalSupplyId === null) return false;
    else return true;
  };

  const isValidDiseaseId = (farmDiseaseId: string | undefined): boolean => {
    if (farmDiseaseId === undefined || farmDiseaseId === '' || farmDiseaseId === null) return false;
    else return true;
  };

  const validatePeriodData = (periodData: ValidationPeriodData): string[] => {
    const errors: string[] = [];

    if (periodData.weeksData?.dayOfWeek === undefined || periodData.weeksData?.dayOfWeek === null)
      errors.push(t('f-x-is-req', { name: t('f-com-day-of-week') }));

    if (periodData.weeksData?.weekCount !== undefined && periodData.weeksData?.weekCount <= 0)
      errors.push(t('x-should-be-higher-than-y', { name: t('f-period-of-notification'), value: 0 }));

    return errors;
  };

  return (
    <>
      <Dialog fullScreen={fullScreen} maxWidth="md" fullWidth open={open} onClose={handleClose} sx={V4_DIALOG_MD}>
        {isLoading ? (
          <>
            <LinearProgress />
            <DialogTitle>{t('f-supp-rule-add')}</DialogTitle>
            <Box sx={{ ml: 4, mr: 4, mb: 4 }}>
              <Skeleton height={60} animation="wave" />
              <Skeleton height={60} animation="wave" />
              <Skeleton height={60} animation="wave" />
            </Box>
          </>
        ) : (
          <>
            <DialogTitle id="ruleDialogTitle">
              {mode === 'create' ? t('f-supp-rule-add') : t('f-supp-rule-edit')}
            </DialogTitle>

            <DialogContent sx={V4_DIALOG_CONTENT}>
              {/* The lung weather rule shows the disease and the medication side by side rather than switching
                  between them, so the region toggle has nothing to say about it. */}
              {selectedTab !== 3 && (
                <Grid item xs={12} sm={6}>
                  <NotificationRuleRegionSelect
                    fullWidth
                    value={notificationRuleRegionType}
                    onChange={(_, value) => resetNotificationRuleRegionType(value)}
                  />
                </Grid>
              )}

              <Tabs
                variant="scrollable"
                scrollButtons="auto"
                value={selectedTab}
                onChange={(_, value) => setSelectedTab(value)}
                indicatorColor="primary"
                textColor="primary"
              >
                <Tab label={t('f-supp-rule-perc')} value={0} />
                <Tab label={t('f-number')} value={1} />
                <Tab
                  label={t('f-notif-rule-min-reg-title')}
                  value={2}
                  disabled={notificationRuleRegionType !== NotificationRuleRegionType.Injection}
                />
                {/* Hidden behind the feature flag, so the rule type cannot be created or edited while it is off. */}
                {lungWeatherEnabled && <Tab label={t(`f-rule-type-${RuleType.LungWeatherCorrelation}`)} value={3} />}
              </Tabs>
              <Divider />

              <Box sx={{ mt: 2 }}>
                {(() => {
                  switch (selectedTab) {
                    case 0:
                      return (
                        <PercentageRuleTab
                          isDisabled={isLoading}
                          animalType={animalType}
                          setAnimalType={setAnimalType}
                          ageRules={ageRules}
                          setAgeRules={setAgeRules}
                          farmSupplyId={farmSupplyId}
                          farmDiseaseId={farmDiseaseId}
                          setFarmSupplyId={setFarmSupplyId}
                          setFarmDiseaseId={setFarmDiseaseId}
                          loadingMedSupp={loadingMedSupp}
                          loadingDiseases={loadingDiseases}
                          farmSupplies={farmSupplies}
                          farmDiseases={farmDiseases}
                          isPregnant={isPregnant}
                          isLactating={isLactating}
                          setIsLactating={setIsLactating}
                          setIsPregnant={setIsPregnant}
                          notificationRuleRegionType={notificationRuleRegionType}
                          allowMultipleRules={mode === 'create'}
                        />
                      );
                    case 1:
                      return (
                        <NumberRuleTab
                          animalType={animalType}
                          setAnimalType={setAnimalType}
                          farmSupplyId={farmSupplyId}
                          farmDiseaseId={farmDiseaseId}
                          setFarmSupplyId={setFarmSupplyId}
                          setFarmDiseaseId={setFarmDiseaseId}
                          farmSupplies={farmSupplies}
                          farmDiseases={farmDiseases}
                          loadingMedSupp={loadingMedSupp}
                          loadingDiseases={loadingDiseases}
                          setIsLactating={setIsLactating}
                          setIsPregnant={setIsPregnant}
                          isLactating={isLactating}
                          isPregnant={isPregnant}
                          minInjected={minInjected}
                          setMinInjected={setMinInjected}
                          dayOfWeek={dayOfWeek}
                          setDayOfWeek={setDayOfWeek}
                          setWeekNumber={setWeekNumber}
                          weekNumber={weekNumber}
                          notificationRuleRegionType={notificationRuleRegionType}
                        />
                      );
                    case 2:
                      return (
                        <MinAnimalRegTab
                          dayOfTheWeek={dayOfWeek}
                          setDayOfTheWeek={setDayOfWeek}
                          weekNumber={weekNumber}
                          setWeekNumber={setWeekNumber}
                          animalType={animalType}
                          setAnimalType={setAnimalType}
                          minRegistered={minRegistered}
                          setMinRegistered={setMinRegistered}
                          notificationRuleRegionType={notificationRuleRegionType}
                        />
                      );
                    case 3:
                      return (
                        <LungWeatherRuleTab
                          isDisabled={isLoading}
                          animalType={animalType}
                          setAnimalType={setAnimalType}
                          farmDiseaseId={farmDiseaseId}
                          setFarmDiseaseId={setFarmDiseaseId}
                          farmDiseases={farmDiseases}
                          loadingDiseases={loadingDiseases}
                          isPregnant={isPregnant}
                          isLactating={isLactating}
                          setIsPregnant={setIsPregnant}
                          setIsLactating={setIsLactating}
                          peakThreshold={peakThreshold}
                          setPeakThreshold={setPeakThreshold}
                          lookbackDays={lookbackDays}
                          setLookbackDays={setLookbackDays}
                          forecastDays={forecastDays}
                          setForecastDays={setForecastDays}
                          sensitivity={sensitivity}
                          setSensitivity={setSensitivity}
                        />
                      );
                  }
                })()}
              </Box>

              {/* Tab 0 runs every week, and tab 3 runs on every pass over a window it takes from its own
                  timescale field - neither has a week count or a weekday to set. */}
              {selectedTab !== 0 && selectedTab !== 3 && (
                <Box sx={{ ml: 1, mr: 1, mt: 2 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <WeekDaySelect dayOfWeek={dayOfWeek} setDayOfWeek={setDayOfWeek} />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('f-period-of-notification')}
                        variant="outlined"
                        fullWidth
                        slotProps={{
                          input: {
                            endAdornment: <InputAdornment position="end">{t('f-com-weeks')}</InputAdornment>
                          }
                        }}
                        size="small"
                        type="number"
                        value={weekNumber}
                        onChange={(e) => setWeekNumber(e.target.value)}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box sx={{ ml: 2, mr: 4, display: 'flex', flexDirection: 'column' }}>
                <UsersStaffSelect
                  users={emails}
                  onModalClose={(users) => {
                    setEmails(users);
                  }}
                />
              </Box>
            </DialogContent>

            <DialogActions>
              <Button
                onClick={handleClose}
                startIcon={<Icon>{'close'}</Icon>}
                disabled={isLoading}
                variant="outlined"
                color="primary"
              >
                {t('f-com-cancel')}
              </Button>

              <Button
                onClick={handleSubmit}
                id="saveBtn"
                startIcon={<Icon>{'done_outline'}</Icon>}
                disabled={isLoading}
                variant="contained"
                color="secondary"
              >
                {renderSubmitButtonText()}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};

export default NotificationRuleDialog;
